import { ref, watch, onMounted, onUnmounted, type Ref } from 'vue';

export interface UseOverlayNavigationOptions {
  /** Forwards the key to the SDK + fires NACX first-interaction/back-press metrics. */
  trackKeyPress: (e: KeyboardEvent) => void;
  /** The overlay timeline's current screen name (e.g. from `useOverlayTimeline`). */
  currentScreen: Ref<string>;
  /** Number of selectable options — read live so it reflects `storeBreak.breakOptions.length`. */
  getOptionCount: () => number;
  /** Screen name where arrow/enter navigation between options is active. Default: 'question'. */
  questionScreen?: string;
  /**
   * Screens reached right after `questionScreen` where the dismiss button can still be
   * arrow-focused (e.g. 'result', or 'result' + 'tidbit' for trivia's extra screen).
   * Default: ['result'].
   */
  postQuestionScreens?: string[];
  /** Whether a dismiss button is present to navigate to/activate. Default: true. */
  hasDismissalButton?: Ref<boolean>;
  /** Called when the user presses Enter on an option (not on the dismiss button). */
  onSelect: () => void;
  /** Called when the user dismisses via the dismiss button. */
  onDismiss: () => void;
  /** Initial focus index. Default: 1 (matches existing poll/trivia behavior). */
  initialActiveIndex?: number;
}

/**
 * Shared keyboard/focus navigation state machine for poll-style overlays
 * (TempPollStreamOverlay, TempTriviaPoll): arrow up/down moves focus through
 * `breakOptions`, wraps into/out of a dismiss button, and Enter either selects
 * the focused option or dismisses. Also auto-freezes navigation (and blanks
 * focus/selection) if the question screen times out with no answer.
 */
export function useOverlayNavigation(options: UseOverlayNavigationOptions) {
  const {
    trackKeyPress,
    currentScreen,
    getOptionCount,
    questionScreen = 'question',
    postQuestionScreens = ['result'],
    hasDismissalButton = ref(true),
    onSelect,
    onDismiss,
    initialActiveIndex = 1,
  } = options;

  const isKeyCurrentlyPressed = ref(false);
  const hasNavigationFrozen = ref(false);
  const hasSelectionBeenMade = ref(false);
  const isDismissInFocus = ref(false);
  const activeIndex = ref(initialActiveIndex);
  const selectedIndex = ref(0);
  const pressedKey = ref('');

  const keyDownHandler = (e: KeyboardEvent) => {
    trackKeyPress(e);
    isKeyCurrentlyPressed.value = true;
    pressedKey.value = e.key;
  };

  const keyUpHandler = () => {
    isKeyCurrentlyPressed.value = false;
    pressedKey.value = '';
  };

  onMounted(() => {
    document.addEventListener('keydown', keyDownHandler);
    document.addEventListener('keyup', keyUpHandler);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', keyDownHandler);
    document.removeEventListener('keyup', keyUpHandler);
  });

  // // When the question screen ends without the user having answered, freeze
  // // navigation and blank focus/selection so nothing appears pre-selected.
  watch(currentScreen, (newState, oldState) => {
    if (
      oldState === questionScreen &&
      postQuestionScreens.includes(newState) &&
      !hasSelectionBeenMade.value
    ) {
      selectedIndex.value = -1;
      activeIndex.value = -1;
      isDismissInFocus.value = false;
      hasNavigationFrozen.value = true;
    }
  });

  watch(isKeyCurrentlyPressed, (newVal) => {
    if (!newVal) return;

    if (currentScreen.value === questionScreen && !hasNavigationFrozen.value) {
      const optionCount = getOptionCount();

      if (pressedKey.value === 'ArrowDown') {
        if (activeIndex.value < optionCount - 1 && activeIndex.value !== -1) {
          activeIndex.value++;
        } else if (activeIndex.value === optionCount - 1 && hasDismissalButton.value) {
          activeIndex.value = -1;
          isDismissInFocus.value = true;
        }
      } else if (pressedKey.value === 'ArrowUp') {
        if (activeIndex.value > 0) {
          activeIndex.value--;
        } else if (activeIndex.value === -1) {
          activeIndex.value = optionCount - 1;
          isDismissInFocus.value = false;
        }
      } else if (pressedKey.value === 'Enter') {
        if (hasDismissalButton.value && isDismissInFocus.value) {
          selectedIndex.value = -1;
          onDismiss();
        } else {
          // Freezing here (not just hasSelectionBeenMade) means callers don't have to
          // remember to also set hasNavigationFrozen themselves after onSelect() fires —
          // locking the question phase on selection is this composable's job.
          hasSelectionBeenMade.value = true;
          hasNavigationFrozen.value = true;
          onSelect();
        }
      }
    } else if (postQuestionScreens.includes(currentScreen.value)) {
      if (pressedKey.value === 'ArrowDown' && hasDismissalButton.value) {
        isDismissInFocus.value = true;
      } else if (
        pressedKey.value === 'Enter' &&
        hasDismissalButton.value &&
        isDismissInFocus.value
      ) {
        onDismiss();
      }
    }
  });

  return {
    activeIndex,
    selectedIndex,
    isDismissInFocus,
    hasNavigationFrozen,
    hasSelectionBeenMade,
  };
}
